import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IBlackboardEntry, IChecklistItem, IChecklistItemCompletion } from '@interfaces/IScheduler';
import { Button } from '@components/ui/Button/Button';
import { CameraIcon } from '@utils/icons';
import { missingEvidence } from '@d-orca/shared/checklist';
import { useChecklistPhotoUpload, type IEvidenceTarget } from '../../hooks/useChecklistPhotoUpload';
import { EvidencePhoto } from './EvidencePhoto';
import { PHOTO_ACCEPT, PHOTO_REFUSAL_KEY } from '../../lib/photoRefusal';
import type { ChecklistEvidenceKind } from '@d-orca/shared/checklist';
import { EvidenceTag } from './ChecklistEvidenceTags';
import styles from './ChecklistCard.module.css';

// What a tick carries, captured before it is made (task-checklist design,
// decisions 7–9). The photo goes up FIRST, to the task's evidence store — the
// tick then references what was stored, and the server refuses one that
// references anything else. The 黒板 arrives pre-filled; every field stays
// editable.

interface IChecklistCaptureFormProps {
  item: IChecklistItem;
  /** Where the photo is stored — this item of the task on its loaded
   *  schedule. Absent (no route context) → no photo can be taken. */
  upload?: IEvidenceTarget;
  blackboard: Required<IBlackboardEntry>;
  checkedByName: string;
  onCommit: (completion: IChecklistItemCompletion) => void;
  onCancel: () => void;
}

const BOARD_FIELDS = ['workType', 'location', 'date'] as const;

export function ChecklistCaptureForm({ item, upload, blackboard, checkedByName, onCommit, onCancel }: IChecklistCaptureFormProps) {
  const { t } = useTranslation();
  const { photo, uploading, failure, take, clear } = useChecklistPhotoUpload(upload);
  const [comment, setComment] = useState('');
  const [board, setBoard] = useState(blackboard);
  const fileRef = useRef<HTMLInputElement>(null);

  const evidence = {
    attachedPhoto: photo,
    comment: comment.trim() || undefined,
    blackboard: item.blackboard === 'none' ? undefined : board,
  };
  const canCommit = !uploading && missingEvidence(item, evidence).length === 0;

  /** A field's heading: its name, and whether the tick waits on it. */
  const heading = (kind: ChecklistEvidenceKind, label: string) => (
    <span className={styles.fieldLabel}>
      {label}
      <EvidenceTag kind={kind} required={item[kind] === 'required'} modeOnly />
    </span>
  );

  return (
    <div className={`${styles.capture} ${styles.indent}`} role="group" aria-label={t('scheduler.panel.checklist.execution.captureNeeded')}>
      <p className={styles.captureNote}>{t('scheduler.panel.checklist.execution.captureNeeded')}</p>

      {item.photo !== 'none' && (
        <div className={styles.field}>
          {heading('photo', t('scheduler.panel.checklist.fields.photo'))}
          {photo ? (
            <span className={styles.thumbRow}>
              <EvidencePhoto photo={photo} className={styles.thumb} alt={photo.name} />
              <Button variant="ghost" size="sm" onClick={clear}>{t('scheduler.panel.checklist.execution.removePhoto')}</Button>
            </span>
          ) : (
            <Button variant="secondary" size="sm" disabled={!take || uploading} onClick={() => fileRef.current?.click()}>
              <CameraIcon size={14} />
              {t(uploading ? 'scheduler.panel.checklist.execution.uploading' : 'scheduler.panel.checklist.execution.addPhoto')}
            </Button>
          )}
          {failure && <span className={styles.error} role="alert">{t(PHOTO_REFUSAL_KEY[failure])}</span>}
          {take && (
            <input
              ref={fileRef}
              type="file"
              accept={PHOTO_ACCEPT}
              capture="environment"
              hidden
              data-testid={`checklist-photo-${item.id}`}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void take(file);
              }}
            />
          )}
        </div>
      )}

      {item.comment !== 'none' && (
        <label className={styles.field}>
          {heading('comment', t('scheduler.panel.checklist.fields.comment'))}
          <textarea
            className={styles.input}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            maxLength={4000}
            placeholder={t('scheduler.panel.checklist.execution.commentPlaceholder')}
            autoFocus={item.photo === 'none'}
          />
        </label>
      )}

      {item.blackboard !== 'none' && (
        <div className={styles.field} role="group" aria-label={t('scheduler.panel.checklist.blackboard.sectionTitle')}>
          {heading('blackboard', t('scheduler.panel.checklist.blackboard.sectionTitle'))}
          <div className={styles.blackboard}>
            {BOARD_FIELDS.map((field) => (
              <label key={field} className={styles.field}>
                <span className={styles.fieldLabel}>{t(`scheduler.panel.checklist.blackboard.${field}`)}</span>
                <input
                  className={styles.input}
                  type={field === 'date' ? 'date' : 'text'}
                  value={board[field]}
                  onChange={(e) => setBoard({ ...board, [field]: e.target.value })}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      <div className={styles.captureFooter}>
        <Button variant="ghost" size="sm" onClick={onCancel}>{t('scheduler.panel.checklist.editorCancel')}</Button>
        <Button
          variant="accent"
          size="sm"
          disabled={!canCommit}
          onClick={() => onCommit({ checked: true, checkedAt: new Date().toISOString(), checkedByName, ...evidence })}
        >
          {t('scheduler.panel.checklist.execution.tick')}
        </Button>
      </div>
    </div>
  );
}
